// frontend/src/ops/PoAnomalies.jsx
// Ops view for Stream 4 Phase 4.B PO anomalies. Lists open flags + one-click
// structured dismiss. Neuzugaenge gehen seit O39b (2026-09-16) in den
// Morgenbericht; diese Seite traegt den BESTAND. Data source:
// /api/purchase_orders/anomalies.

import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'
import {
  Paper, Stack, Typography, Chip, Button, Box, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Link as MuiLink,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import RefreshButton from '../components/common/RefreshButton'
import { describeStockLayerMissing, STOCK_LAYER_MISSING_LABEL, STOCK_LAYER_MISSING_NEXT_STEP } from '../utils/stockLayerAnomaly'
import { lsmMismatchNextStep } from '../utils/lsmStatusMismatch'
import { formatUnitCompact } from '../utils/format'
import DisabledReason from '../components/common/DisabledReason';
import TableSkeleton from '../Skeletons/TableSkeleton'

const API = process.env.REACT_APP_API_BASE_URL || ''

const TYPE_LABEL = {
  date_slip:                'Vendor announced later delivery',
  no_vendor_comms:          'No vendor comms past expected',
  lsm_pog_status_mismatch:  'LSM booked · POG not updated',
  stock_layer_missing:      STOCK_LAYER_MISSING_LABEL,
}
const TYPE_COLOR = {
  date_slip: 'warning',
  no_vendor_comms: 'info',
  lsm_pog_status_mismatch: 'error',
  stock_layer_missing: 'warning',
}

const DISMISS_CATEGORIES = [
  { value: 'vendor_responded', label: 'Vendor responded' },
  { value: 'known_delay',      label: 'Known delay' },
  { value: 'manual_override',  label: 'Manual override' },
  { value: 'false_positive',   label: 'False positive' },
  { value: 'other',            label: 'Other (please explain)' },
]

// EK-A21: vendor_responded / known_delay make no sense for a missing FIFO layer,
// and dismissing it is permanent while the same lines stay without a layer.
const DISMISS_BY_TYPE = {
  stock_layer_missing: {
    categories: [
      { value: 'manual_override', label: 'FIFO layer added manually' },
      { value: 'false_positive',  label: 'False positive' },
      { value: 'other',           label: 'Other (please explain)' },
    ],
    note: 'Stays dismissed as long as the same lines have no FIFO layer.',
    detail: STOCK_LAYER_MISSING_NEXT_STEP,
  },
}

function fmtRelative(iso) {
  if (!iso) return '—'
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60)    return `${Math.floor(diff)}s ago`
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function DismissDialog({ open, anomaly, onClose, onSubmit, busy }) {
  const [category, setCategory] = useState('')
  const [reason, setReason] = useState('')
  useEffect(() => { if (open) { setCategory(''); setReason('') } }, [open])
  const canSubmit = category && (category !== 'other' || reason.trim())
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Dismiss anomaly</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {anomaly?.po_id} · {TYPE_LABEL[anomaly?.anomaly_type] || anomaly?.anomaly_type}
          </Typography>
          <TextField
            select
            label="Category"
            size="small"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {(DISMISS_BY_TYPE[anomaly?.anomaly_type]?.categories || DISMISS_CATEGORIES).map(c => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          {/* N6 (Folgebahn): what to do first, what dismissing means second */}
          {DISMISS_BY_TYPE[anomaly?.anomaly_type]?.detail && (
            <Typography variant="caption" color="text.secondary">
              {DISMISS_BY_TYPE[anomaly.anomaly_type].detail}
            </Typography>
          )}
          {DISMISS_BY_TYPE[anomaly?.anomaly_type]?.note && (
            <Typography variant="caption" color="text.secondary">
              {DISMISS_BY_TYPE[anomaly.anomaly_type].note}
            </Typography>
          )}
          <TextField
            label={category === 'other' ? 'Reason (required)' : 'Reason (optional)'}
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required={category === 'other'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <DisabledReason
          reason={busy
            ? null
            : !category ? 'Select a category first.'
              : (category === 'other' && !reason.trim())
                ? 'Enter a reason for the "Other" category first.' : null}
        >
          <Button
            variant="contained"
            onClick={() => onSubmit({ category, reason: reason.trim() || null })}
            disabled={!canSubmit || busy}
          >
            {busy ? 'Dismissing…' : 'Dismiss'}
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}

export default function PoAnomalies() {
  // 🔴 R16 N1: `items` startet auf `null`, nicht auf `[]`. „Noch nie geladen"
  // ist etwas anderes als „die Liste ist leer": mit `[]` laese der erste Neulauf
  // einer berechtigt leeren Liste als Erstaufbau, und der Knopf sperrte dann,
  // statt zu drehen.
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [typeFilter, setTypeFilter] = useState('all')
  const [runningDetection, setRunningDetection] = useState(false)
  const [dismissTarget, setDismissTarget] = useState(null)
  const [dismissBusy, setDismissBusy] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const { data } = await axios.get(`${API}/api/purchase_orders/anomalies`, {
        params: typeFilter === 'all' ? {} : { type: typeFilter },
      })
      setItems(data || [])
    } catch (err) {
      setError(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }, [typeFilter])

  useEffect(() => { fetchAll() }, [fetchAll])

  const counts = useMemo(() => {
    const rows = items ?? []
    const all = rows.length
    const perType = {}
    // 🔴 ZEILEN und VORGAENGE sind hier zwei Groessen. `po_anomalies` traegt Dubletten:
    // `_write_anomaly` prueft-dann-schreibt ohne UNIQUE, und bis `5e14e08f2` (11.09.)
    // feuerte jeder In-Process-Job doppelt. Der Erzeuger ist gestoppt, der Rueckstand
    // nicht. Wer Zeilen zaehlt, ueberzeichnet — an prod gemessen um 55 %.
    // Gruppiert wird hier ABSICHTLICH nicht: ein Wegdruecken muesste dann beide
    // Zwillinge treffen, sonst kehrt die Zeile zurueck, und das ist eine
    // Verhaltensaenderung an einer Liste, an der Geld haengt. Sie bekommt eine eigene
    // Runde. Bis dahin wird die Differenz BENANNT statt verschwiegen.
    const paare = new Set()
    for (const it of rows) {
      perType[it.anomaly_type] = (perType[it.anomaly_type] || 0) + 1
      paare.add(`${it.po_id}\u0000${it.anomaly_type}`)
    }
    return { all, perType, vorgaenge: paare.size }
  }, [items])

  const handleRunDetection = useCallback(async () => {
    setRunningDetection(true)
    try {
      const { data } = await axios.post(`${API}/api/purchase_orders/anomalies/run-detection`)
      setError(null)
      await fetchAll()
      // eslint-disable-next-line no-alert
      alert(`Detection run: flagged ${data.flagged || 0} new (per_type: ${JSON.stringify(data.per_type)})`)
    } catch (err) {
      setError(err?.response?.data?.error || err.message)
    } finally { setRunningDetection(false) }
  }, [fetchAll])

  const handleDismiss = useCallback(async ({ category, reason }) => {
    if (!dismissTarget) return
    setDismissBusy(true)
    try {
      await axios.post(
        `${API}/api/purchase_orders/anomalies/${dismissTarget.id}/dismiss`,
        { category, reason },
      )
      setDismissTarget(null)
      await fetchAll()
    } catch (err) {
      setError(err?.response?.data?.error || err.message)
    } finally { setDismissBusy(false) }
  }, [dismissTarget, fetchAll])

  return (
    <PageContainer>
      {/* 🔴 K-11 (UI-Durchgang 2 Teil 6, 2026-10-10): „Run detection now" ist
          eine SEITENWEITE Handlung und stand in der Filterzeile — sie gehoert in
          den Kopf (`actions`). Die Zeile darunter traegt Filter und Auffrischen.
          Der Kopf traegt hier GENAU EINE Aktion, deshalb rendert er sie ohne
          weiteren Eintrag als `contained` — und das ist richtig: der Detektorlauf
          ist die eine Handlung, die diese Seite veranlasst, und er ist nicht
          destruktiv (`pattern-library.md` §Kopfaktionsmodell Nr. 3). Die Probe:
          `frontend/tests/design/ui2-k-11-kopfaktionen.spec.ts`.
          🔴 Der `RefreshButton` daneben BLEIBT in der Zeile (W13/E8,
          2026-10-07): ein Auffrischen ist nie die Primaeraktion und gehoert
          dorthin, wo die Daten liegen. */}
      <ViewHeader
        title="PO Anomalies"
        subtitle="Open anomalies flagged by the Stream 4 detector. New ones also appear in the morning briefing — the standing list lives here. Process flags on closed or deleted purchase orders are hidden."
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Purchase Orders', href: '/overview' }, { label: 'PO Anomalies' }]}
        actions={[
          {
            key: 'run-detection',
            label: runningDetection ? 'Running…' : 'Run detection now',
            emphasis: 'primary',
            icon: <PlayArrowIcon />,
            onClick: handleRunDetection,
            disabled: runningDetection,
          },
        ]}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        {['all', ...Object.keys(TYPE_LABEL)].map(t => (
          <Chip
            key={t}
            label={
              t === 'all'
                ? (counts.vorgaenge === counts.all
                    ? `All · ${counts.all}`
                    // 🔴 ENGLISCH. Die erste Fassung schrieb „N Vorgänge (M Zeilen)" — deutscher
                    // Satzteil mitten in einer durchgehend englischen Seite, laut UX-Gate das
                    // einzige Vorkommen dieser Art im ganzen Frontend. Der Hinweis war richtig
                    // und in der falschen Sprache, also unverstaendlich fuer die Mehrheit.
                    : `All · ${counts.vorgaenge} (${counts.all} rows)`)
                : `${TYPE_LABEL[t]} · ${counts.perType[t] || 0}`
            }
            onClick={() => setTypeFilter(t)}
            color={typeFilter === t ? 'primary' : 'default'}
            variant={typeFilter === t ? 'filled' : 'outlined'}
            title={
              t === 'all' && counts.vorgaenge !== counts.all
                ? `${counts.vorgaenge} distinct (PO, type) cases across ${counts.all} rows. `
                  + 'Duplicate rows are a known backlog item: the detector wrote every flag '
                  + 'twice until 2026-09-11. The producer is fixed, the backlog of rows is not.'
                : undefined
            }
          />
        ))}
        <Box sx={{ flex: 1 }} />
        {/* 🔴 W13 (2026-10-07): beschrifteter „Refresh" neben „Run detection
            now" — zwei Formen fuer dieselbe Sache, und die eine davon startet
            einen Lauf statt eine Ansicht zu laden. Der reine Neulauf ist jetzt
            der eine Auffrisch-Knopf (E8).
            `firstLoad`: der Aufbau zeigt unten ein Geruest (K-S), nicht mehr
            einen `CircularProgress`.
            🔴 R16 N1: der Diskriminator ist `items === null` („noch nie
            geladen"), nicht `items.length === 0` („Liste leer").
            🔴 K-11 (2026-10-10): „Run detection now" steht nicht mehr hier —
            sie ist eine seitenweite Handlung und sitzt jetzt in den Kopfaktionen
            (`ViewHeader actions`). */}
        <RefreshButton
          ariaLabel="Refresh anomalies"
          loading={loading}
          firstLoad={loading && items === null}
          onClick={fetchAll}
        />
      </Stack>

      {loading ? (
        // K-S (UI-Durchgang 2): Seitenaufbau als Geruest der geladenen
        // Tabelle (5 Spalten), nicht als Kreisel. `paper` spiegelt ihren
        // Rahmen (`Paper variant="outlined"`, Zeile darunter) statt `Panel` —
        // Schatten und Radius waeren sonst beim Eintreffen der Daten ein Sprung.
        <TableSkeleton paper columns={5} rows={5} ariaLabel="Loading anomalies" />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Detected</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(items ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {/* 🔴 Im Fehlerfall NICHT „Nice." sagen: daneben steht dann ein Alert,
                        und eine leere Tabelle heisst hier „nicht geladen", nicht „nichts da".
                        Regel 17 §3b auf der Oberflaeche — ein Werkzeug, das nicht lief, sah
                        bisher aus wie eines, das nichts fand. */}
                    {error
                      ? 'Could not load anomalies — see the message above.'
                      : typeFilter === 'all'
                        ? 'No open anomalies on active purchase orders.'
                        : `No open anomalies of this type on active purchase orders. Pick “All” to see the other types.`}
                  </TableCell>
                </TableRow>
              ) : (items ?? []).map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <MuiLink component={RouterLink} to={`/purchase_order/${a.po_id}`}>
                      {a.po_id}
                    </MuiLink>
                    {a.po_status && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.po_status}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={TYPE_LABEL[a.anomaly_type] || a.anomaly_type}
                      color={TYPE_COLOR[a.anomaly_type] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{fmtRelative(a.detected_at)}</TableCell>
                  <TableCell>
                    {a.anomaly_type === 'date_slip' && a.details && (
                      <Typography variant="caption">
                        vendor says <b>{a.details.vendor_announced_date}</b>,
                        PO expects <b>{a.details.expected_arrival}</b>
                        (<b>+{a.details.slip_days}d slip</b>)
                      </Typography>
                    )}
                    {a.anomaly_type === 'no_vendor_comms' && a.details && (
                      <Typography variant="caption">
                        expected <b>{a.details.expected_arrival}</b>.{' '}
                        {/* 🔴 U3 (01.11.2026): „noch kein Kontakt" ist ein eigener
                            Zustand, keine Zahl. `last_inbound`/`last_sent` sind
                            fuer „kein Kontakt" `null` — daran heilen auch die
                            ALTZEILEN, die noch 20714 tragen, ohne Schreibvorgang
                            auf `po_anomalies`. Dieselbe Regel steht in
                            `PODetail.jsx` (Anomalie-Kaestchen). */}
                        {!a.details.last_inbound && !a.details.last_sent
                          ? 'No vendor contact recorded yet.'
                          : (typeof a.details.days_since_last_comms === 'number'
                              ? <>last comms {formatUnitCompact(a.details.days_since_last_comms)} ago</>
                              : 'Vendor contact recorded.')}
                      </Typography>
                    )}
                    {a.anomaly_type === 'lsm_pog_status_mismatch' && a.details && (
                      <Typography variant="caption">
                        LSM booked {a.details.hours_stale}h ago · PO still <b>{a.details.po_status}</b>
                      </Typography>
                    )}
                    {/* R16-W6: bei `In Delivery` steht der naechste Schritt darunter.
                        Bei `Order placed`/`Paid` bleibt es beim alten Text. */}
                    {a.anomaly_type === 'lsm_pog_status_mismatch' && lsmMismatchNextStep(a.details) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {lsmMismatchNextStep(a.details)}
                      </Typography>
                    )}
                    {a.anomaly_type === 'stock_layer_missing' && (() => {
                      const s = describeStockLayerMissing(a.details)
                      return (
                        <>
                          <Typography variant="caption" display="block" title={STOCK_LAYER_MISSING_NEXT_STEP}>
                            {s.summary || '—'}
                          </Typography>
                          {s.skus && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              SKUs: {s.skus}
                            </Typography>
                          )}
                        </>
                      )
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setDismissTarget(a)}>Dismiss</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <DismissDialog
        open={!!dismissTarget}
        anomaly={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onSubmit={handleDismiss}
        busy={dismissBusy}
      />
    </PageContainer>
  )
}
